<template>
  <div class="message-center">
    <div class="message-header">
      <div class="header-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          <line x1="9" y1="9" x2="15" y2="9"/>
          <line x1="9" y1="13" x2="13" y2="13"/>
        </svg>
      </div>
      <h1>{{ isAdmin ? 'Üzenet Központ' : 'Tanár Üzenet Központ' }}</h1>
      <p>{{ isAdmin ? 'Küldj üzeneteket csoportoknak vagy egyéni táncosoknak' : 'Üzenet küldés a hozzárendelt csoportoknak' }}</p>
    </div>

    <div class="message-content">
      <!-- Message Type Selection -->
      <div class="message-types">
        <div class="type-tabs">
          <button 
            class="tab-button" 
            :class="{ active: messageType === 'group' }"
            @click="messageType = 'group'"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Csoport Üzenet
          </button>
          <button 
            class="tab-button" 
            :class="{ active: messageType === 'child' }"
            @click="messageType = 'child'"
            v-if="isAdmin || isTeacher"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Egyéni Táncos
          </button>
          <button 
            class="tab-button" 
            :class="{ active: messageType === 'bulk' }"
            @click="messageType = 'bulk'"
            v-if="isAdmin"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6,9 6,2 18,2 18,9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Tömeges Üzenet
          </button>
          <button 
            class="tab-button tab-button--debt" 
            :class="{ active: messageType === 'debt' }"
            @click="messageType = 'debt'"
            v-if="isAdmin || isTeacher"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Tartozás Értesítés
            <span v-if="debtors.length > 0" class="debt-tab-badge">
              {{ debtors.length }}
            </span>
          </button>
        </div>
      </div>

      <!-- Group Message Form -->
      <div v-if="messageType === 'group'" class="message-form">
        <div class="form-group">
          <label>Válassz csoportot</label>
          <SearchableSelect
            v-model="selectedGroup"
            :options="groups"
            placeholder="Válassz egy csoportot..."
            search-placeholder="Keresés csoportok között..."
            :multiple="false"
            label-key="name"
            value-key="id"
          />
        </div>

        <div class="form-group">
          <label>Tárgy</label>
          <input 
            type="text" 
            v-model="subject" 
            placeholder="Üzenet tárgya"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label>Üzenet</label>
          <textarea 
            v-model="message" 
            placeholder="Írd be az üzenetet..."
            rows="6"
            class="form-textarea"
          ></textarea>
        </div>

        <div class="form-actions">
          <button 
            @click="sendGroupMessage" 
            :disabled="!canSendGroup || sending"
            class="btn btn-primary"
          >
            <span v-if="sending">Küldés...</span>
            <span v-else>Üzenet küldése</span>
          </button>
        </div>
      </div>

      <!-- Child Message Form -->
      <div v-if="messageType === 'child'" class="message-form">
        <div class="form-group">
          <label>Válassz táncost</label>
          <SearchableSelect
            v-model="selectedChild"
            :options="formattedDancers"
            placeholder="Válassz egy táncost..."
            search-placeholder="Keresés táncosok között..."
            :multiple="false"
            label-key="displayName"
            value-key="id"
          />
        </div>

        <div class="form-group">
          <label>Tárgy</label>
          <input 
            type="text" 
            v-model="subject" 
            placeholder="Üzenet tárgya"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label>Üzenet</label>
          <textarea 
            v-model="message" 
            placeholder="Írd be az üzenetet..."
            rows="6"
            class="form-textarea"
          ></textarea>
        </div>

        <div class="form-actions">
          <button 
            @click="sendChildMessage" 
            :disabled="!canSendChild || sending"
            class="btn btn-primary"
          >
            <span v-if="sending">Küldés...</span>
            <span v-else>Üzenet küldése</span>
          </button>
        </div>
      </div>

      <!-- Bulk Message Form -->
      <div v-if="messageType === 'bulk'" class="message-form">
        <div class="bulk-selection">
          <div class="form-group">
            <label>Csoportok kiválasztása</label>
            <SearchableSelect
              v-model="selectedGroups"
              :options="groups"
              placeholder="Válassz csoportokat..."
              search-placeholder="Keresés csoportok között..."
              :multiple="true"
              label-key="name"
              value-key="id"
            />
          </div>

          <div class="form-group">
            <label>Táncosok kiválasztása</label>
            <SearchableSelect
              v-model="selectedChildren"
              :options="formattedDancers"
              placeholder="Válassz táncosokat..."
              search-placeholder="Keresés táncosok között..."
              :multiple="true"
              label-key="displayName"
              value-key="id"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Tárgy</label>
          <input 
            type="text" 
            v-model="subject" 
            placeholder="Üzenet tárgya"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label>Üzenet</label>
          <textarea 
            v-model="message" 
            placeholder="Írd be az üzenetet..."
            rows="6"
            class="form-textarea"
          ></textarea>
        </div>

        <div class="form-actions">
          <button 
            @click="sendBulkMessage" 
            :disabled="!canSendBulk || sending"
            class="btn btn-primary"
          >
            <span v-if="sending">Küldés...</span>
            <span v-else>Tömeges üzenet küldése</span>
          </button>
        </div>
      </div>

      <!-- Debt Notification Form -->
      <div v-if="messageType === 'debt'" class="message-form debt-form">
        <!-- Mode Switcher & Stats Bar -->
        <div class="debt-header-box">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <span class="debt-count-badge">
                  {{ debtors.length }} hátralékos táncos
                </span>
                <span class="text-muted small">
                  Rendezetlen tagdíjjal / hátralékkal rendelkező diákok
                </span>
              </div>
              <p class="text-muted small mb-0 mt-1">
                Értesítsd a tagdíj hátralékkal vagy rendezetlen tételekkel rendelkező diákokat/szülőket egyénileg vagy csoportosan.
              </p>
            </div>

            <!-- Single vs Group Switcher -->
            <div class="mode-switch-group">
              <button 
                type="button" 
                class="mode-switch-btn" 
                :class="{ active: debtSendMode === 'single' }" 
                @click="debtSendMode = 'single'"
              >
                Egyéni (Egyenként)
              </button>
              <button 
                type="button" 
                class="mode-switch-btn" 
                :class="{ active: debtSendMode === 'group' }" 
                @click="debtSendMode = 'group'"
              >
                Csoportos (Több táncos)
              </button>
            </div>
          </div>
        </div>

        <!-- Filter by Group (applicable in both modes) -->
        <div class="form-group" v-if="groupsWithDebtors.length > 1">
          <label>Csoport szűrése</label>
          <select v-model.number="debtFilterGroup" class="form-select">
            <option :value="0">Összes csoport ({{ debtors.length }} hátralékos)</option>
            <option v-for="g in groupsWithDebtors" :key="g.id" :value="g.id">
              {{ g.name }} ({{ g.count }} hátralékos)
            </option>
          </select>
        </div>

        <!-- Single Mode Debtor Selector -->
        <div v-if="debtSendMode === 'single'" class="form-group">
          <label>Válassz hátralékos táncost</label>
          <SearchableSelect
            v-model="selectedDebtorId"
            :options="formattedDebtors"
            placeholder="Válassz egy tartozással rendelkező táncost..."
            search-placeholder="Keresés hátralékos táncosok között..."
            :multiple="false"
            label-key="displayName"
            value-key="id"
          />

          <!-- Selected Debtor Detail Card -->
          <div v-if="selectedDebtor" class="debtor-detail-card">
            <div>
              <div class="debtor-detail-name">{{ selectedDebtor.full_name }}</div>
              <div class="debtor-detail-meta">
                Csoport: <strong>{{ selectedDebtor.group_name || 'Nincs csoport' }}</strong>
                &bull; Értesítendő email(ek): 
                <span v-if="selectedDebtor.emails && selectedDebtor.emails.length" class="debtor-detail-email">
                  {{ selectedDebtor.emails.join(', ') }}
                </span>
                <span v-else class="text-danger">Nincs rögzített email cím!</span>
              </div>
            </div>
            <div>
              <span class="debt-status-badge">
                Rendezetlen hátralék
              </span>
            </div>
          </div>
        </div>

        <!-- Group Mode Debtors Table -->
        <div v-if="debtSendMode === 'group'" class="form-group">
          <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <label class="mb-0">
              Címzettek kijelölése 
              <span class="text-muted small">({{ selectedDebtorIds.length }} / {{ filteredDebtors.length }} kijelölve)</span>
            </label>
            <div class="d-flex gap-2">
              <button type="button" class="btn-pill-subtle" @click="selectAllDebtors">
                Összes kijelölése
              </button>
              <button type="button" class="btn-pill-subtle" @click="deselectAllDebtors">
                Kijelölés törlése
              </button>
            </div>
          </div>

          <div class="debtors-table-wrap">
            <table class="debtors-table">
              <thead>
                <tr>
                  <th style="width: 40px;" class="ps-3">
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      :checked="isAllDebtorsSelected" 
                      @change="toggleSelectAllDebtors" 
                    />
                  </th>
                  <th>Táncos neve</th>
                  <th>Csoport</th>
                  <th class="text-center" style="width: 140px;">Státusz</th>
                  <th class="text-end pe-3">Művelet</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in filteredDebtors" :key="d.id" :class="{ selected: selectedDebtorIds.includes(d.id) }" style="cursor: pointer;" @click="toggleDebtorSelection(d.id)">
                  <td class="ps-3" @click.stop>
                    <input 
                      type="checkbox" 
                      class="form-check-input" 
                      :value="d.id" 
                      v-model="selectedDebtorIds" 
                    />
                  </td>
                  <td class="dancer-name">{{ d.full_name }}</td>
                  <td class="dancer-group">{{ d.group_name || 'Nincs csoport' }}</td>
                  <td class="text-center">
                    <span class="debt-status-badge debt-status-badge--sm">
                      Rendezetlen
                    </span>
                  </td>
                  <td class="text-end pe-3" @click.stop>
                    <button 
                      type="button" 
                      class="btn-outline-action" 
                      @click="selectSingleDebtor(d)"
                      title="Csak ennek a táncosnak a külön értesítése"
                    >
                      Egyéni
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredDebtors.length === 0">
                  <td colspan="5" class="text-center py-4 text-muted">
                    Nincs hátralékos táncos a megadott szűrési feltételek mellett.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Predefined Message Templates Dropdown -->
        <div class="form-group">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <label class="mb-0">Előre megírt sablon betöltése</label>
            <span class="text-muted small">Válassz sablont, amit kedved szerint módosíthatsz</span>
          </div>
          <select v-model="selectedTemplateKey" @change="onTemplateChange" class="form-select">
            <option value="default">Alapértelmezett barátságos emlékeztető</option>
            <option value="formal">Határidős hivatalos felszólítás</option>
            <option value="short">Rövid tömör értesítő</option>
            <option value="custom">Egyedi üres sablon</option>
          </select>
        </div>

        <!-- Subject Textbox -->
        <div class="form-group">
          <label>Üzenet tárgya</label>
          <input 
            type="text" 
            v-model="debtSubject" 
            placeholder="pl. Tagdíj hátralék értesítés - {táncos_neve}"
            class="form-input"
          />
        </div>

        <!-- Message Textbox (Editable for Admins to write their own stuff) -->
        <div class="form-group">
          <div class="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
            <label class="mb-0">Üzenet szövege</label>
            <div class="d-flex align-items-center gap-1.5 flex-wrap">
              <span class="text-muted small me-1">Változók beillesztése:</span>
              <button 
                type="button" 
                class="tag-pill-btn" 
                @click="insertTag('{táncos_neve}')"
                title="Kattints a beillesztéshez"
              >
                + {táncos_neve}
              </button>
              <button 
                type="button" 
                class="tag-pill-btn" 
                @click="insertTag('{csoport}')"
                title="Kattints a beillesztéshez"
              >
                + {csoport}
              </button>
            </div>
          </div>
          <textarea 
            v-model="debtMessage" 
            placeholder="Írd ide a saját üzeneted, vagy egészítsd ki az előre megírt szöveget..."
            rows="8"
            class="form-textarea"
          ></textarea>
          <small class="text-muted">
            A <code>{táncos_neve}</code> és <code>{csoport}</code> mezők automatikusan helyettesítésre kerülnek az adott diák valós adataival.
          </small>
        </div>

        <!-- Live Preview Box -->
        <div v-if="previewDebtor" class="preview-box">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="preview-badge">
              Élő előnézet: {{ previewDebtor.full_name }} részére
            </span>
            <span class="text-muted small">Státusz: Rendezetlen hátralék</span>
          </div>
          <div class="preview-subject">
            Tárgy: {{ previewSubject }}
          </div>
          <div class="preview-body">
            {{ previewMessage }}
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <button 
            @click="sendDebtNotification" 
            :disabled="!canSendDebt || sending"
            class="btn btn-primary"
          >
            <span v-if="sending">Értesítések küldése...</span>
            <span v-else-if="debtSendMode === 'single'">
              Értesítés küldése ({{ selectedDebtor ? selectedDebtor.full_name : '1 táncos' }})
            </span>
            <span v-else>
              Csoportos értesítés küldése ({{ selectedDebtorIds.length }} kijelölt táncosnak)
            </span>
          </button>
        </div>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>

      <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import SearchableSelect from './shared/SearchableSelect.vue';

export default {
  name: 'MessageCenterItem',
  components: {
    SearchableSelect
  },
  computed: {
    ...mapGetters(['isAdmin', 'isTeacher']),
    
    canSendGroup() {
      return this.selectedGroup && this.subject.trim() && this.message.trim();
    },
    canSendChild() {
      return this.selectedChild && this.subject.trim() && this.message.trim();
    },
    canSendBulk() {
      return (this.selectedGroups.length > 0 || this.selectedChildren.length > 0) && 
             this.subject.trim() && this.message.trim();
    },
    canSendDebt() {
      if (this.debtSendMode === 'single') {
        return !!this.selectedDebtorId && this.debtSubject.trim() !== '' && this.debtMessage.trim() !== '';
      }
      return this.selectedDebtorIds.length > 0 && this.debtSubject.trim() !== '' && this.debtMessage.trim() !== '';
    },
    totalDebtAmount() {
      return (this.debtors || []).reduce((acc, d) => acc + (d.debt_amount || 0), 0);
    },
    filteredDebtors() {
      if (!this.debtFilterGroup) {
        return this.debtors || [];
      }
      return (this.debtors || []).filter(d => d.group_id === this.debtFilterGroup);
    },
    formattedDebtors() {
      return (this.filteredDebtors || []).map(d => ({
        ...d,
        displayName: `${d.full_name} (${d.group_name || 'Nincs csoport'} - Rendezetlen hátralék)`
      }));
    },
    groupsWithDebtors() {
      const map = new Map();
      (this.debtors || []).forEach(d => {
        if (d.group_id && d.group_name) {
          if (!map.has(d.group_id)) {
            map.set(d.group_id, { id: d.group_id, name: d.group_name, count: 0 });
          }
          map.get(d.group_id).count++;
        }
      });
      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    selectedDebtor() {
      return (this.debtors || []).find(d => d.id === this.selectedDebtorId) || null;
    },
    previewDebtor() {
      if (this.debtSendMode === 'single' && this.selectedDebtor) {
        return this.selectedDebtor;
      }
      if (this.selectedDebtorIds.length > 0) {
        return (this.debtors || []).find(d => d.id === this.selectedDebtorIds[0]) || this.filteredDebtors[0] || null;
      }
      return this.filteredDebtors[0] || null;
    },
    previewSubject() {
      if (!this.previewDebtor) return this.debtSubject;
      return this.interpolateText(this.debtSubject, this.previewDebtor);
    },
    previewMessage() {
      if (!this.previewDebtor) return this.debtMessage;
      return this.interpolateText(this.debtMessage, this.previewDebtor);
    },
    isAllDebtorsSelected() {
      return this.filteredDebtors.length > 0 && 
             this.filteredDebtors.every(d => this.selectedDebtorIds.includes(d.id));
    },
    
    formattedDancers() {
      return this.dancers.map(dancer => ({
        ...dancer,
        displayName: `${dancer.family_name} ${dancer.sure_name} (${dancer.group_name || 'Nincs csoport'})`
      }));
    }
  },
  data() {
    return {
      messageType: 'group',
      groups: [],
      dancers: [],
      debtors: [],
      debtSendMode: 'single',
      selectedDebtorId: '',
      selectedDebtorIds: [],
      debtFilterGroup: 0,
      selectedTemplateKey: 'default',
      debtSubject: '',
      debtMessage: '',
      selectedGroup: '',
      selectedChild: '',
      selectedGroups: [],
      selectedChildren: [],
      subject: '',
      message: '',
      sending: false,
      successMessage: '',
      errorMessage: ''
    };
  },
  watch: {
    selectedGroup(newVal, oldVal) {
      console.log('selectedGroup changed from:', oldVal, 'to:', newVal);
    },
    selectedChild(newVal, oldVal) {
      console.log('selectedChild changed from:', oldVal, 'to:', newVal);
    },
    selectedGroups(newVal, oldVal) {
      console.log('selectedGroups changed from:', oldVal, 'to:', newVal);
    },
    selectedChildren(newVal, oldVal) {
      console.log('selectedChildren changed from:', oldVal, 'to:', newVal);
    }
  },
  methods: {
    async loadGroups() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/message/groups');
        this.groups = response.data.groups || [];
      } catch (error) {
        console.error('Error loading groups:', error);
        this.errorMessage = 'Nem sikerült betölteni a csoportokat';
      }
    },

    async loadDancers() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/message/dancers');
        this.dancers = response.data.dancers || [];
      } catch (error) {
        console.error('Error loading dancers:', error);
        this.errorMessage = 'Nem sikerült betölteni a táncosokat';
      }
    },

    async sendGroupMessage() {
      if (!this.canSendGroup) return;

      this.sending = true;
      this.clearMessages();

      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/message/group', {
          type: 'group',
          group_id: this.selectedGroup,
          subject: this.subject,
          message: this.message
        });

        this.successMessage = response.data.message + 
          ` (${response.data.recipients} címzett)`;
        this.resetForm();
      } catch (error) {
        console.error('Error sending group message:', error);
        this.errorMessage = error.response?.data?.message || 'Hiba történt az üzenet küldése során';
      } finally {
        this.sending = false;
      }
    },

    async sendChildMessage() {
      if (!this.canSendChild) return;

      this.sending = true;
      this.clearMessages();

      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/message/child', {
          type: 'child',
          child_id: this.selectedChild,
          subject: this.subject,
          message: this.message
        });

        this.successMessage = response.data.message + 
          ` (${response.data.recipients} címzett)`;
        this.resetForm();
      } catch (error) {
        console.error('Error sending child message:', error);
        this.errorMessage = error.response?.data?.message || 'Hiba történt az üzenet küldése során';
      } finally {
        this.sending = false;
      }
    },

    async sendBulkMessage() {
      if (!this.canSendBulk) return;

      this.sending = true;
      this.clearMessages();

      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/message/bulk', {
          group_ids: this.selectedGroups,
          child_ids: this.selectedChildren,
          subject: this.subject,
          message: this.message
        });

        this.successMessage = response.data.message + 
          ` (${response.data.recipients} egyedi címzett)`;
        this.resetForm();
      } catch (error) {
        console.error('Error sending bulk message:', error);
        this.errorMessage = error.response?.data?.message || 'Hiba történt az üzenet küldése során';
      } finally {
        this.sending = false;
      }
    },

    resetForm() {
      this.selectedGroup = '';
      this.selectedChild = '';
      this.selectedGroups = [];
      this.selectedChildren = [];
      this.subject = '';
      this.message = '';
      this.selectedDebtorId = '';
      this.selectedDebtorIds = [];
    },

    clearMessages() {
      this.successMessage = '';
      this.errorMessage = '';
    },

    async loadDebtors() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/message/debtors');
        this.debtors = response.data.debtors || [];
        if (this.debtors.length > 0 && !this.selectedDebtorId) {
          this.selectedDebtorId = this.debtors[0].id;
        }
      } catch (error) {
        console.error('Error loading debtors:', error);
      }
    },

    formatPrice(val) {
      if (val === null || val === undefined) return '0 Ft';
      return Math.round(Number(val)).toLocaleString('hu-HU') + ' Ft';
    },

    applyTemplate(key) {
      this.selectedTemplateKey = key;
      if (key === 'default') {
        this.debtSubject = 'Tagdíj hátralék értesítés - {táncos_neve}';
        this.debtMessage = `Kedves Szülő / Táncos!

Tájékoztatjuk, hogy nyilvántartásunk szerint {táncos_neve} fiókjában rendezetlen tagdíj hátralék áll fenn ({csoport}).

Kérjük, szíveskedjen a hátralévő összeget mielőbb rendezni az oktatónál vagy banki átutalással.

Amennyiben a befizetést a közelmúltban már teljesítette, kérjük tekintse levelünket tárgytalannak.

Együttműködésüket és segítségüket köszönjük!

Üdvözlettel,
Tánciskola Vezetősége`;
      } else if (key === 'formal') {
        this.debtSubject = 'Felszólítás elmaradt tagdíj rendezésére - {táncos_neve}';
        this.debtMessage = `Tisztelt Szülő / Táncos!

Értesítjük, hogy nyilvántartásunk alapján {táncos_neve} ({csoport}) részére rendezetlen díjtartozás van nyilvántartva.

Kérjük a fennálló hátralék mielőbbi rendezését. Kérdés esetén forduljon bizalommal az iskola vezetőségéhez.

Üdvözlettel,
Tánciskola Vezetősége`;
      } else if (key === 'short') {
        this.debtSubject = 'Tagdíj emlékeztető: {táncos_neve}';
        this.debtMessage = `Kedves Szülő / Táncos!

Szeretnénk emlékeztetni, hogy {táncos_neve} táncos fiókjában jelenleg rendezetlen tagdíj hátralék van. Kérjük az összeg rendezését.

Köszönjük!`;
      } else if (key === 'custom') {
        this.debtSubject = 'Tájékoztatás tagdíjról - {táncos_neve}';
        this.debtMessage = `Kedves Szülő / Táncos!

`;
      }
    },

    onTemplateChange() {
      this.applyTemplate(this.selectedTemplateKey);
    },

    insertTag(tag) {
      if (!this.debtMessage) {
        this.debtMessage = tag;
      } else {
        this.debtMessage += ' ' + tag;
      }
    },

    interpolateText(text, debtor) {
      if (!text || !debtor) return text || '';
      const name = debtor.full_name || '';
      const debt = this.formatPrice(debtor.debt_amount || 0);
      const group = debtor.group_name || '';

      return text
        .replace(/\{táncos_neve\}/g, name)
        .replace(/\{tancos_neve\}/g, name)
        .replace(/\{dancer_name\}/g, name)
        .replace(/\{tancos\}/g, name)
        .replace(/\{táncos\}/g, name)
        .replace(/\{name\}/g, name)
        .replace(/\{tartozás_összege\}/g, debt)
        .replace(/\{tartozas_osszege\}/g, debt)
        .replace(/\{debt_amount\}/g, debt)
        .replace(/\{tartozas\}/g, debt)
        .replace(/\{tartozás\}/g, debt)
        .replace(/\{összeg\}/g, debt)
        .replace(/\{osszeg\}/g, debt)
        .replace(/\{amount\}/g, debt)
        .replace(/\{csoport\}/g, group)
        .replace(/\{group_name\}/g, group);
    },

    selectAllDebtors() {
      this.selectedDebtorIds = this.filteredDebtors.map(d => d.id);
    },

    deselectAllDebtors() {
      this.selectedDebtorIds = [];
    },

    toggleDebtorSelection(id) {
      const idx = this.selectedDebtorIds.indexOf(id);
      if (idx > -1) {
        this.selectedDebtorIds.splice(idx, 1);
      } else {
        this.selectedDebtorIds.push(id);
      }
    },

    toggleSelectAllDebtors() {
      if (this.isAllDebtorsSelected) {
        this.deselectAllDebtors();
      } else {
        this.selectAllDebtors();
      }
    },

    selectSingleDebtor(debtor) {
      this.debtSendMode = 'single';
      this.selectedDebtorId = debtor.id;
    },

    async sendDebtNotification() {
      if (!this.canSendDebt) return;

      this.sending = true;
      this.clearMessages();

      try {
        const payload = {
          mode: this.debtSendMode,
          child_id: this.debtSendMode === 'single' ? Number(this.selectedDebtorId) : 0,
          child_ids: this.debtSendMode === 'group' ? this.selectedDebtorIds : [],
          subject: this.debtSubject,
          message: this.debtMessage
        };

        const response = await axios.post(this.$BACKEND_HOST + '/api/message/debt-notification', payload);
        this.successMessage = response.data.message || 'Értesítés(ek) sikeresen elküldve!';
        if (response.data.failed && response.data.failed.length > 0) {
          this.errorMessage = 'Figyelmeztetés: néhány diák nem kapott üzenetet: ' + response.data.failed.join(', ');
        }
        await this.loadDebtors();
      } catch (error) {
        console.error('Error sending debt notification:', error);
        this.errorMessage = error.response?.data?.message || 'Hiba történt a tartozás értesítés küldése során';
      } finally {
        this.sending = false;
      }
    }
  },
  mounted() {
    // Load real data from API
    this.loadGroups();
    if (this.isAdmin || this.isTeacher) {
      this.loadDancers();
      this.loadDebtors();
      this.applyTemplate('default');
    }
  },
  watch: {
    messageType(newVal) {
      this.clearMessages();
      this.resetForm();
      if (newVal === 'debt') {
        this.loadDebtors();
        if (!this.debtSubject) {
          this.applyTemplate('default');
        }
      }
    }
  }
};
</script>

<style scoped>
.message-center {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.message-header {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  text-align: center;
  box-shadow: var(--shadow-lg);
  margin-bottom: 2rem;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.message-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.message-header p {
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
}

.message-content {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  padding: 2rem;
}

.message-types {
  margin-bottom: 2rem;
}

.type-tabs {
  display: flex;
  gap: 0.5rem;
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 0.5rem;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 500;
}

.tab-button:hover {
  background: var(--color-background);
  color: var(--color-text);
}

.tab-button.active {
  background: var(--primary-blue);
  color: white;
  box-shadow: var(--shadow-sm);
}

.message-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--color-heading);
  font-size: 0.9rem;
}

.form-select,
.form-input,
.form-textarea {
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.form-select:focus,
.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(var(--primary-blue-rgb), 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  background: var(--color-background-mute);
  border-radius: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--color-background);
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-weight: normal;
}

.checkbox-label:hover {
  background: var(--color-border);
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: var(--primary-blue);
}

.bulk-selection {
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: var(--primary-blue);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--secondary-blue);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-primary:disabled {
  background: var(--color-text-muted);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.alert {
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-weight: 500;
}

.alert-success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #15803d;
}

.alert-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #dc2626;
}

.tab-button--debt {
  position: relative;
}

.debt-tab-badge {
  background: #ef4444;
  color: white;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  margin-left: 0.35rem;
}

.debt-header-box {
  background: var(--color-background-mute);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 0.5rem;
}

.debt-count-badge {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.3);
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
}

[data-theme="dark"] .debt-count-badge {
  background: rgba(239, 68, 68, 0.25);
  color: #fca5a5;
  border-color: rgba(239, 68, 68, 0.4);
}

.mode-switch-group {
  display: flex;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  padding: 4px;
  border-radius: 10px;
  gap: 4px;
}

.mode-switch-btn {
  border: none;
  background: transparent;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all 0.2s ease;
}

.mode-switch-btn:hover {
  color: var(--color-text);
  background: var(--hover-bg);
}

.mode-switch-btn.active {
  background: var(--primary-blue);
  color: white;
  box-shadow: var(--shadow-sm);
}

.debtor-detail-card {
  background: var(--card-bg);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-top: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.debtor-detail-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-heading);
}

.debtor-detail-meta {
  font-size: 0.85rem;
  color: var(--color-text-muted);
  margin-top: 0.2rem;
}

.debtor-detail-email {
  color: var(--primary-blue);
  font-weight: 500;
}

.debt-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.25);
  padding: 0.35rem 0.85rem;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.85rem;
}

.debt-status-badge--sm {
  padding: 0.2rem 0.65rem;
  font-size: 0.75rem;
}

[data-theme="dark"] .debt-status-badge {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
  border-color: rgba(239, 68, 68, 0.4);
}

.btn-pill-subtle {
  border: 1px solid var(--color-border);
  background: var(--card-bg);
  color: var(--color-text);
  border-radius: 20px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-pill-subtle:hover {
  background: var(--color-background-mute);
  border-color: var(--color-border-hover);
}

.btn-outline-action {
  border: 1px solid var(--primary-blue);
  background: transparent;
  color: var(--primary-blue);
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-outline-action:hover {
  background: var(--primary-blue);
  color: white;
}

.debtors-table-wrap {
  border: 1px solid var(--color-border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--card-bg);
  max-height: 280px;
  overflow-y: auto;
}

.debtors-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
  font-size: 0.9rem;
}

.debtors-table th {
  background: var(--color-background-mute);
  color: var(--color-heading);
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: 1;
}

.debtors-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  background: var(--card-bg);
  vertical-align: middle;
}

.debtors-table tr:hover td {
  background: var(--color-background-mute);
}

.debtors-table tr.selected td {
  background: rgba(37, 99, 235, 0.06);
}

.debtors-table .dancer-name {
  font-weight: 600;
  color: var(--color-heading);
}

.debtors-table .dancer-group {
  color: var(--color-text-muted);
  font-size: 0.85rem;
}

.tag-pill-btn {
  border: 1px dashed var(--color-border);
  background: var(--card-bg);
  color: var(--primary-blue);
  padding: 0.25rem 0.65rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tag-pill-btn:hover {
  background: rgba(37, 99, 235, 0.08);
  border-color: var(--primary-blue);
}

.preview-box {
  background: var(--color-background-mute);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 1.25rem;
  margin-top: 0.5rem;
}

.preview-badge {
  background: var(--color-text-muted);
  color: var(--card-bg);
  border-radius: 9999px;
  padding: 0.2rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 600;
}

.preview-subject {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-heading);
  margin-bottom: 0.5rem;
}

.preview-body {
  color: var(--color-text);
  background: var(--card-bg);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 1rem;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.6;
}

/* Responsive design */
@media (max-width: 768px) {
  .message-center {
    padding: 1rem;
  }
  
  .message-header {
    padding: 1.5rem;
  }
  
  .message-content {
    padding: 1.5rem;
  }
  
  .type-tabs {
    flex-direction: column;
  }
  
  .tab-button {
    justify-content: center;
  }
}
</style>